import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type BulkRowDraft = {
  id: string;
  serviceId?: string | null;
  address: string;
  checkoutAt: Date | null;
  checkinAt: Date | null;
  windowStartAt: Date | null;
  windowEndAt: Date | null;
  guests: number | null;
  hasPets: boolean | null;
  hasKids: boolean | null;
  notes: string | null;
  warnings?: string[];
  errors?: string[];
};

function fmt(dt: Date | null): string {
  if (!dt) return "";
  try {
    const iso = dt.toISOString();
    // yyyy-mm-ddThh:mm:ssZ -> yyyy-mm-ddThh:mm
    return iso.slice(0, 16);
  } catch {
    return "";
  }
}

function parseLocalDateTime(v: string): Date | null {
  const s = (v || "").trim();
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function fmtHuman(dt: Date | null): string {
  if (!dt) return "—";
  try {
    return dt.toLocaleString();
  } catch {
    return "—";
  }
}

export default function BulkImportTable({
  rows,
  onChange,
  bufferMinutes,
  services,
}: {
  rows: BulkRowDraft[];
  onChange: (rows: BulkRowDraft[]) => void;
  bufferMinutes: number;
  services: Array<{ id: string; name: string }>;
}) {
  const updateRow = (id: string, patch: Partial<BulkRowDraft>) => {
    onChange(
      rows.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        // Recompute window if both dates exist
        if (next.checkoutAt && next.checkinAt) {
          next.windowStartAt = next.checkoutAt;
          next.windowEndAt = new Date(next.checkinAt.getTime() - Math.max(0, bufferMinutes) * 60_000);
        }
        // Basic validation (client-side hints)
        const errors: string[] = [];
        if (!next.serviceId) errors.push("Servizio mancante");
        if (!next.address || next.address.trim().length < 6) errors.push("Indirizzo mancante o troppo corto");
        if (!next.checkoutAt) errors.push("Checkout non valido");
        if (!next.checkinAt) errors.push("Check-in non valido");
        if (next.windowStartAt && next.windowEndAt && next.windowEndAt.getTime() <= next.windowStartAt.getTime()) {
          errors.push("Finestra non valida (check-in troppo vicino al checkout)");
        }
        next.errors = errors;
        return next;
      })
    );
  };

  const removeRow = (id: string) => onChange(rows.filter((r) => r.id !== id));

  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground">Nessuna riga.</div>;
  }

  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const hasErrors = (r.errors?.length || 0) > 0;
        return (
          <div
            key={r.id}
            className={cn(
              "border rounded-xl p-3 space-y-2",
              hasErrors ? "border-red-300 bg-red-50/40" : "border-gray-200"
            )}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Indirizzo</div>
                <Input
                  value={r.address}
                  onChange={(e) => updateRow(r.id, { address: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                {hasErrors ? <Badge variant="destructive">Errore</Badge> : <Badge variant="secondary">OK</Badge>}
                <Button size="sm" variant="outline" onClick={() => removeRow(r.id)}>
                  Rimuovi
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Servizio</div>
              <Select
                value={(r.serviceId || "") as string}
                onValueChange={(v) => updateRow(r.id, { serviceId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona un servizio" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground" title="Data e ora in cui gli ospiti attualmente in casa escono.">
                  Checkout <span className="text-[10px] text-slate-400">(uscita ospiti attuali)</span>
                </div>
                <Input
                  type="datetime-local"
                  value={fmt(r.checkoutAt)}
                  onChange={(e) => updateRow(r.id, { checkoutAt: parseLocalDateTime(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground" title="Data e ora in cui i prossimi ospiti arrivano.">
                  Check-in <span className="text-[10px] text-slate-400">(arrivo prossimi ospiti)</span>
                </div>
                <Input
                  type="datetime-local"
                  value={fmt(r.checkinAt)}
                  onChange={(e) => updateRow(r.id, { checkinAt: parseLocalDateTime(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Ospiti</div>
                <Input
                  type="number"
                  value={r.guests ?? ""}
                  onChange={(e) => updateRow(r.id, { guests: e.target.value ? Number(e.target.value) : null })}
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Note</div>
                <Input
                  value={r.notes ?? ""}
                  onChange={(e) => updateRow(r.id, { notes: e.target.value || null })}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">
                Finestra calcolata: <span className="font-medium">{fmtHuman(r.windowStartAt)}</span> →{" "}
                <span className="font-medium">{fmtHuman(r.windowEndAt)}</span>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox
                    checked={!!r.hasPets}
                    onCheckedChange={(v) => updateRow(r.id, { hasPets: Boolean(v) })}
                  />
                  Animali
                </label>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox
                    checked={!!r.hasKids}
                    onCheckedChange={(v) => updateRow(r.id, { hasKids: Boolean(v) })}
                  />
                  Bambini
                </label>
              </div>
            </div>

            {(r.errors?.length || 0) > 0 && (
              <div className="text-xs text-red-700">
                {r.errors?.map((e, idx) => (
                  <div key={idx}>- {e}</div>
                ))}
              </div>
            )}
            {(r.warnings?.length || 0) > 0 && (
              <div className="text-xs text-amber-700">
                {r.warnings?.map((w, idx) => (
                  <div key={idx}>- {w}</div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

